import type { TasksMapPerson, TasksMapPoint } from '@/api/tasksMap'
import { loadGoogleMaps } from '@/utils/googleMapsLoader'
import {
  createCondensedPersonDotIcon,
  createHiddenAnchorIcon,
  createOrderNumberIcon,
  createPersonInitialsIcon,
  createSelectedPersonAnchorIcon,
  TASKS_MAP_SELECTED_MAX_ZOOM,
  TASKS_MAP_SELECTED_MIN_SPAN,
} from './tasksMapMapIcons'
import { TASKS_MAP_DEFAULT_OPTIONS } from './tasksMapMapStyle'

type LatLngPoint = google.maps.LatLngLiteral

function isValidMapPoint(point: LatLngPoint): boolean {
  if (!Number.isFinite(point.lat) || !Number.isFinite(point.lng)) return false
  if (point.lat === 0 && point.lng === 0) return false
  return Math.abs(point.lat) <= 90 && Math.abs(point.lng) <= 180
}

function isSamePoint(a: LatLngPoint, b: LatLngPoint, epsilon = 1e-5): boolean {
  return Math.abs(a.lat - b.lat) < epsilon && Math.abs(a.lng - b.lng) < epsilon
}

/**
 * 人物定位与 order 重合时拉开距离，保证连线清晰可见（街景级缩放下约数百米）。
 */
function resolvePersonAnchorPosition(
  personPosition: LatLngPoint,
  orderPositions: LatLngPoint[],
): LatLngPoint {
  if (!orderPositions.length) return personPosition
  const overlapsOrder = orderPositions.some((orderPos) => isSamePoint(personPosition, orderPos))
  if (!overlapsOrder) return personPosition

  return {
    lat: personPosition.lat + 0.0045,
    lng: personPosition.lng + 0.0035,
  }
}

export class TasksMapMapController {
  private map: google.maps.Map | null = null
  private routePolyline: google.maps.Polyline | null = null
  private markers: google.maps.Marker[] = []
  private mapClickListener: google.maps.MapsEventListener | null = null
  private boundsListener: google.maps.MapsEventListener | null = null
  private onMapClick: (() => void) | null = null

  async init(container: HTMLElement): Promise<google.maps.Map> {
    await loadGoogleMaps()
    this.map = new google.maps.Map(container, TASKS_MAP_DEFAULT_OPTIONS)
    return this.map
  }

  setMapClickHandler(handler: (() => void) | null): void {
    this.onMapClick = handler
    this.mapClickListener?.remove()
    this.mapClickListener = null

    if (!this.map || !handler) return

    this.mapClickListener = this.map.addListener('click', () => {
      handler()
    })
  }

  /**
   * 选中态（如图）：人物 16 实心圆 + 全名；orders 编号 32；白线连接；其他人缩点仍画但不强行进视野。
   * selectedOrderId：点击侧栏子任务后，在对应订单点旁显示完整地址（样式同人名标签）。
   */
  render(
    people: TasksMapPerson[],
    selectedPersonId: string,
    selectedRoute: TasksMapPoint[] = [],
    selectedOrderId = '',
  ): void {
    const map = this.map
    if (!map) return

    this.clearOverlays()

    const hasSelection = Boolean(selectedPersonId)
    const activePerson = people.find((person) => person.id === selectedPersonId) ?? null

    const sortedOrders = activePerson
      ? [...activePerson.orders]
          .filter((order) => isValidMapPoint(order.position))
          .sort((a, b) => a.sequence - b.sequence)
      : []

    const personAnchor =
      activePerson && isValidMapPoint(activePerson.position)
        ? resolvePersonAnchorPosition(
            activePerson.position,
            sortedOrders.map((order) => order.position),
          )
        : null

    people.forEach((person) => {
      if (!isValidMapPoint(person.position)) return
      const isActive = person.id === selectedPersonId
      const position = isActive && personAnchor ? personAnchor : person.position

      let icon: google.maps.Icon | google.maps.Symbol
      if (isActive) {
        icon = createSelectedPersonAnchorIcon()
      } else if (hasSelection) {
        icon = createCondensedPersonDotIcon(person.color)
      } else {
        icon = createPersonInitialsIcon(person.initials)
      }

      this.markers.push(
        new google.maps.Marker({
          map,
          position,
          zIndex: isActive ? 25 : 10,
          icon,
          clickable: true,
        }),
      )
    })

    if (!activePerson || !personAnchor) {
      this.fitBoundsToPoints(people.map((person) => person.position).filter(isValidMapPoint))
      return
    }

    // 人物 → order1 → order2… 全路径；保证多订单都编号、连线、进入视野
    const routeFromOrders: LatLngPoint[] = [
      personAnchor,
      ...sortedOrders.map((order) => order.position),
    ]
    const backendRoute = selectedRoute.filter(isValidMapPoint)
    // 后端路线：若只有订单点、未含人物点，前面补上人物锚点
    const backendRouteWithPerson =
      backendRoute.length > 0 && !isSamePoint(backendRoute[0]!, personAnchor)
        ? [personAnchor, ...backendRoute]
        : backendRoute.length > 0
          ? backendRoute
          : []

    // 后端路线点数不足以覆盖全部订单时，改用订单序列，避免漏画后面的点
    const routePath =
      backendRouteWithPerson.length >= routeFromOrders.length &&
      backendRouteWithPerson.length > 1 &&
      !isSamePoint(backendRouteWithPerson[0]!, backendRouteWithPerson[1]!)
        ? backendRouteWithPerson
        : routeFromOrders

    if (routePath.length > 1) {
      this.routePolyline = new google.maps.Polyline({
        map,
        path: routePath,
        geodesic: true,
        strokeColor: '#ffffff',
        strokeOpacity: 1,
        strokeWeight: 3,
        zIndex: 5,
      })
    }

    const selectedOrder =
      selectedOrderId && activePerson
        ? activePerson.orders.find((order) => order.id === selectedOrderId)
        : undefined
    const selectedOrderPosition =
      selectedOrder && isValidMapPoint(selectedOrder.position) ? selectedOrder.position : null
    const selectedOrderAddress = selectedOrder?.address?.trim() ?? ''

    // 订单缺坐标但后端给了路线点时，仍画出编号点，保证真实数据也能连线
    if (sortedOrders.length > 0) {
      sortedOrders.forEach((order, index) => {
        const isOrderSelected = Boolean(selectedOrderId) && order.id === selectedOrderId
        this.markers.push(
          new google.maps.Marker({
            map,
            position: order.position,
            zIndex: isOrderSelected ? 45 : 30 + index,
            icon: createOrderNumberIcon(index + 1),
          }),
        )

        if (isOrderSelected && order.address.trim()) {
          this.markers.push(
            new google.maps.Marker({
              map,
              position: order.position,
              zIndex: 50,
              icon: createHiddenAnchorIcon(),
              label: {
                text: order.address.trim(),
                color: '#000000',
                fontFamily: "'SF Pro', SFPro, -apple-system, BlinkMacSystemFont, system-ui, sans-serif",
                fontSize: '12px',
                fontWeight: 'bold',
                className: 'tm-map-name-label',
              },
            }),
          )
        }
      })
    } else {
      backendRoute
        .filter((point) => !isSamePoint(point, personAnchor))
        .forEach((position, index) => {
          this.markers.push(
            new google.maps.Marker({
              map,
              position,
              zIndex: 30 + index,
              icon: createOrderNumberIcon(index + 1),
            }),
          )
        })
    }

    this.markers.push(
      new google.maps.Marker({
        map,
        position: personAnchor,
        zIndex: 40,
        icon: createHiddenAnchorIcon(),
        label: {
          text: activePerson.name,
          color: '#000000',
          fontFamily: "'SF Pro', SFPro, -apple-system, BlinkMacSystemFont, system-ui, sans-serif",
          fontSize: '12px',
          fontWeight: 'bold',
          className: 'tm-map-name-label',
        },
      }),
    )

    // 点了子任务：视野优先落在该订单地址；否则人物 + 全部订单
    if (selectedOrderPosition && selectedOrderAddress) {
      this.fitSelectedRoute([personAnchor, selectedOrderPosition])
      return
    }

    const orderMarkerPositions =
      sortedOrders.length > 0
        ? sortedOrders.map((order) => order.position)
        : backendRoute.filter((point) => !isSamePoint(point, personAnchor))
    const fitPoints =
      routePath.length > 1
        ? routePath
        : [personAnchor, ...orderMarkerPositions].filter(isValidMapPoint)
    this.fitSelectedRoute(fitPoints)
  }

  destroy(): void {
    this.clearOverlays()
    this.mapClickListener?.remove()
    this.mapClickListener = null
    this.onMapClick = null
    this.map = null
  }

  private clearOverlays(): void {
    this.boundsListener?.remove()
    this.boundsListener = null
    this.routePolyline?.setMap(null)
    this.routePolyline = null
    this.markers.forEach((marker) => marker.setMap(null))
    this.markers.length = 0
  }

  private fitBoundsToPoints(points: LatLngPoint[]): void {
    const map = this.map
    if (!map || points.length === 0) return

    if (points.length === 1) {
      map.setCenter(points[0]!)
      map.setZoom(14)
      return
    }

    const bounds = new google.maps.LatLngBounds()
    points.forEach((point) => bounds.extend(point))
    map.fitBounds(bounds, 80)
  }

  /** 选中路线专用视野：人物 + 全部订单都进框；多点分散时允许缩小，不裁切 */
  private fitSelectedRoute(points: LatLngPoint[]): void {
    const map = this.map
    if (!map || points.length === 0) return

    if (points.length === 1) {
      map.setCenter(points[0]!)
      map.setZoom(15)
      return
    }

    const bounds = new google.maps.LatLngBounds()
    points.forEach((point) => bounds.extend(point))

    const ne = bounds.getNorthEast()
    const sw = bounds.getSouthWest()
    const latSpan = Math.abs(ne.lat() - sw.lat())
    const lngSpan = Math.abs(ne.lng() - sw.lng())
    const isCompact =
      latSpan < TASKS_MAP_SELECTED_MIN_SPAN && lngSpan < TASKS_MAP_SELECTED_MIN_SPAN
    if (isCompact) {
      const center = bounds.getCenter()
      const half = TASKS_MAP_SELECTED_MIN_SPAN / 2
      bounds.extend({ lat: center.lat() + half, lng: center.lng() + half })
      bounds.extend({ lat: center.lat() - half, lng: center.lng() - half })
    }

    map.fitBounds(bounds, { top: 100, right: 160, bottom: 100, left: 100 })

    this.boundsListener?.remove()
    this.boundsListener = map.addListener('idle', () => {
      this.boundsListener?.remove()
      this.boundsListener = null
      const zoom = map.getZoom()
      // 仅紧凑路线限制最大缩放；多订单跨度大时绝不再拉近（否则会裁掉远处 order）
      if (zoom != null && isCompact && zoom > TASKS_MAP_SELECTED_MAX_ZOOM) {
        map.setZoom(TASKS_MAP_SELECTED_MAX_ZOOM)
      }
    })
  }
}
